import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  DefaultButton, PrimaryButton, Panel, PanelType,
  Overlay, Text, Icon,
} from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { findParentElementWithClass } from '@bingads-webui-campaign-react/asset-library-model';
import { MetaDataEditor } from './meta-data-editor';
import { VideoSettingsEditor } from './video-settings-editor';
import { getVideoEditorStyles } from './styles';
import { ScenePickerView } from './scene-picker/view';
import { PreviewView } from './preview/view';
import { useEditVideoAdsViewState } from './state';


export const EditVideoAdsView = ({
  onApply,
  isOpen,
  setIsOpen,
  videoMetaData,
  cid,
  aid,
  oDataConfig,
  finalUrl,
  assetLibraryConfig,
  dataService,
  copilotDataService,
  onChangeImage,
  newImageDataObject,
  onChangeLogo,
  newLogoDataObject,
  logger,
  saveVideoWhenApply,
  isCTV,
}) => {
  const classes = useDefaultStyles(getVideoEditorStyles);
  const i18n = useContext(I18nContext);
  const {
    disableApply,
    disableGenerate,
    handleGenerate,
    selectedSceneId,
    onSceneSelected,
    scenes,
    selectedScene,
    onScenesUpdated,
    video,
    currentPanel,
    switchPanel,
    isScenePanel,
    sceneGenerated,
    videoGenerated,
    isApplying,
    handleApply,
    generateError,
    setGenerateError,
    textErrorForSelectedScene,
    minSizeOfImage,
    onVideoSettingsSelected,
    videoSettings,
    setVideoSettings,
    businessNameErrorMessage,
  } = useEditVideoAdsViewState({
    videoMetaData,
    dataService,
    copilotDataService,
    onApply,
    setIsOpen,
    logger,
    i18n,
    newImageDataObject,
    newLogoDataObject,
    saveVideoWhenApply,
    isCTV,
  });

  const handleTextListChange = useCallback((textList) => {
    const updatedScenes = scenes.map((scene) => {
      if (scene.id === selectedSceneId) {
        return {
          ...scene,
          updating: {
            ...scene.updating,
            text: textList,
          },
        };
      }
      return scene;
    });
    onScenesUpdated(updatedScenes);
  }, [scenes, selectedSceneId, onScenesUpdated]);

  const onDismiss = useCallback((evt) => {
    const target = evt && evt.currentTarget;
    const panelElem = findParentElementWithClass(target, classes.videoEditor);
    if (panelElem || evt.key === 'Escape') {
      setIsOpen(false);
      logger.logTrace('Copilot video editor: Panel dismissed');
    }
  }, [classes.videoEditor, logger, setIsOpen]);

  return (
    <Panel
      className={classNames('asset-library-video-editor-view', classes.videoEditor)}
      headerText={i18n.getString(_TL_('Edit your video'))}
      isOpen={isOpen}
      type={PanelType.custom}
      onDismiss={onDismiss}
      customWidth="1275px"
      layerProps={{
        styles: {
          root: {
            zIndex: 900,
          },
        },
      }}
      styles={{
        root: {
          top: 60,
        },
        main: {
          overflowY: 'hidden',
        },
      }}
    >
      {isApplying && (
        <Overlay styles={{ root: { zIndex: 1 } }}>
          <AssetLibrarySpinner className="page-load-spinner" useCustomSize />
        </Overlay>
      )}
      <div className={classNames(classes.top, 'view-top')}>
        <div className={classNames(classes.leftPanel, 'left-panel')}>
          <div className={classNames(classes.leftPanelTop, 'left-panel-top')}>
            <div />
          </div>
          <div className={classNames(classes.leftPanelMiddle, 'left-panel-middle')}>
            <div className={classNames(classes.tabs, 'tabs')}>
              <div className={classNames(classes.videoSettings, 'video-settings')}>
                <div className="item-title">
                  <Text variant="smallPlus" >{i18n.getString(_TL_('Video settings'))}</Text>
                </div>
                <div className={classNames('video-settings-content', selectedSceneId === -1 && 'selected')} aria-label={i18n.getString(_TL_('Video Settings Button'))} onClick={onVideoSettingsSelected} onKeyDown={() => {}} tabIndex={0} role="button">
                  <Icon iconName="Settings" className="icon" />
                </div>
              </div>
              {
                scenes &&
                <ScenePickerView
                  scenes={scenes}
                  selectedSceneId={selectedSceneId}
                  onSceneSelected={onSceneSelected}
                />
              }
            </div>
            {
                scenes.length > 0 && scenes.map((scene) => {
                  const isSelected = scene.id === selectedSceneId;
                  return (
                    <div className={classNames(classes.metadata, 'metadata', isSelected && 'selected')} key={scene.id}>
                      {
                      scene && scene.updating &&
                      <MetaDataEditor
                        metaData={{ ...scene.updating, newImage: scene.newImage }}
                        i18n={i18n}
                        cid={cid}
                        aid={aid}
                        oDataConfig={oDataConfig}
                        finalUrl={finalUrl}
                        assetLibraryConfig={assetLibraryConfig}
                        onChangeImage={() => onChangeImage(minSizeOfImage[0])}
                        onTextListChange={handleTextListChange}
                        handleGenerate={handleGenerate}
                        disableGenerate={disableGenerate}
                        textErrorMessages={textErrorForSelectedScene}
                      />
                    }
                    </div>
                  );
                })
              }
            <div className={classNames(classes.metadata, 'metadata', selectedSceneId === -1 && 'selected')}>
              {
                selectedSceneId === -1 &&
                <VideoSettingsEditor
                  videoSettings={videoSettings}
                  setVideoSettings={setVideoSettings}
                  i18n={i18n}
                  onLoadingLogo={() => {}}
                  onChangeLogo={onChangeLogo}
                  newLogoDataObject={newLogoDataObject}
                  disableGenerate={disableGenerate}
                  handleGenerate={handleGenerate}
                  businessNameErrorMessage={businessNameErrorMessage}
                />
              }
            </div>
          </div>
          <div className={classNames(classes.leftPanelBottom, 'left-panel-bottom')}>
            <div />
          </div>
        </div>
        <div className={classNames(classes.rightPanel, 'right-panel')}>
          <PreviewView // display the keyFrame and the video
            scene={selectedScene.effective}
            video={video}
            sceneGenerated={sceneGenerated}
            videoGenerated={videoGenerated}
            currentPanel={currentPanel}
            switchPanel={switchPanel}
            isScenePanel={isScenePanel}
            generateError={generateError}
            setGenerateError={setGenerateError}
          />
        </div>
      </div>
      <div className={classNames(classes.bottom, 'view-bottom')}>
        <div className="button-container">
          <PrimaryButton
            className="apply-button"
            text={i18n.getString(_TL_('Apply'))}
            onClick={handleApply}
            disabled={disableApply}
          />
          <DefaultButton
            className="cancel-button"
            text={i18n.getString(_TL_('Cancel'))}
            onClick={() => {
              setIsOpen(false);
              logger.logTrace('Copilot video editor: Cancel button clicked');
            }}
          />
        </div>
      </div>
    </Panel>);
};

EditVideoAdsView.propTypes = {
  onApply: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  videoMetaData: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.string),
    text: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  finalUrl: PropTypes.string.isRequired,
  assetLibraryConfig: PropTypes.shape({}).isRequired,
  dataService: PropTypes.shape({}).isRequired,
  copilotDataService: PropTypes.shape({}).isRequired,
  onChangeImage: PropTypes.func.isRequired,
  newImageDataObject: PropTypes.shape({
    url: PropTypes.string,
  }),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  onChangeLogo: PropTypes.func.isRequired,
  newLogoDataObject: PropTypes.shape({
    url: PropTypes.string,
  }),
  saveVideoWhenApply: PropTypes.bool,
  isCTV: PropTypes.bool,
};

EditVideoAdsView.defaultProps = {
  newImageDataObject: {},
  newLogoDataObject: {},
  saveVideoWhenApply: false,
  isCTV: false,
};
